// ============================================================================
// save-path — the one way a write proves it landed and tells the screen.
//
// WHY THIS EXISTS (Ben's ruling, 2026-09-03)
//
// Four tickets arrived saying some version of "it won't save" or "it isn't
// showing". Two of them were real saves that reached the database while the
// screen kept the old value; the person did the right thing, saw no evidence
// of it, and did it again. The audit trail on one shows identical values
// re-saved six seconds apart — the signature of someone who believes the
// first save didn't take.
//
// The cause is that this application has no convention. 321 call sites write
// to the database and five different things happen afterwards. Three of the
// five are legitimate. Which one a screen uses depends on who built it.
//
// There is NO TanStack Query here (verified: absent from package.json; STATE
// says "no TanStack Query — useEffect loaders"). So there are no query keys to
// invalidate. Reads are useEffect loaders and Zustand. This module is the
// convention that replaces the missing one, and it does exactly two things,
// because two distinct failures produced those four tickets:
//
//   1. PROVES THE WRITE LANDED.  A PostgREST update or delete that matches
//      ZERO rows returns success with no error. `.update(x).eq(pk, id)` with
//      no `.select()` cannot tell "I changed the row" from "I changed
//      nothing" — an RLS policy that permits the read and refuses the write
//      is silent, and the caller's optimistic value stays on screen forever.
//      A zero-row protected write is a FAILURE, not a no-op (the principle
//      TCSD-0122 established, applied here to the client). So every write
//      through this module asks for its rows back and refuses an empty answer.
//
//   2. TELLS THE SURFACES THAT READ IT.  A write declares the tables it
//      touched; every loader registered against those tables re-reads. The
//      writer does not need to know which screens exist, which is the whole
//      point — the next screen someone builds is refreshed by a write that
//      predates it.
//
// WHAT THIS MODULE DELIBERATELY DOES NOT DO
//
// - It does not flip a loading flag. A refetch keeps current data on screen
//   while it runs; a loading flag that is also a remount signal destroys the
//   card that is mid-save (TCSD-0039).
// - It does not set page-level error state. It THROWS, so the modal or card
//   that started the write reports the outcome itself and keeps the user's
//   typed text (TCSD-0044).
// - It does not replace Realtime. The New Accounts module subscribes to
//   postgres_changes and merges rows, which also solves two people editing at
//   once. That is a stronger mechanism where it is adopted; this is the floor
//   under every screen that has not adopted it.
// ============================================================================

import { useEffect } from 'react';
import { describeError, type DescribedError } from './errors';

/**
 * A table name a write can declare and a loader can register against. Plain
 * strings on purpose: a write in one module must be able to refresh a reader
 * in another without importing it, and the set grows with the schema.
 */
export type ReaderTable = string;

type Refresh = () => void;

const readers = new Map<ReaderTable, Set<Refresh>>();

/**
 * Register a loader's refresh function against the tables it reads. Returns
 * the unregister function. Prefer the `useReader` hook in components.
 */
export function registerReader(tables: readonly ReaderTable[], refresh: Refresh): () => void {
  for (const t of tables) {
    let set = readers.get(t);
    if (!set) {
      set = new Set();
      readers.set(t, set);
    }
    set.add(refresh);
  }
  return () => {
    for (const t of tables) readers.get(t)?.delete(refresh);
  };
}

/**
 * Re-read every loader registered against any of `tables`. Safe to call with
 * tables nobody reads — that is the normal case for an audit-only insert.
 *
 * One loader registered against two of the tables refreshes ONCE: a save that
 * touches a facility and its assignment must not double-fetch the screen
 * showing both.
 */
export function refreshReaders(tables: readonly ReaderTable[]): void {
  const seen = new Set<Refresh>();
  for (const t of tables) {
    const set = readers.get(t);
    if (!set) continue;
    for (const fn of set) seen.add(fn);
  }
  for (const fn of seen) {
    try {
      fn();
    } catch (err) {
      // A broken reader must never make a write that already committed look
      // like it failed. Never swallow to NULL (§8.8) — say so.
      console.error('[save-path] a registered reader threw on refresh', err);
    }
  }
}

/** Test seam. Not for application code. */
export function __clearReadersForTest(): void {
  readers.clear();
}

/**
 * The shape supabase-js returns from a write with `.select()`. Kept structural
 * rather than importing PostgrestResponse so this module does not fight the
 * generated types (which a parallel workstream owns).
 */
export interface WriteResult<T> {
  data: T[] | null;
  error: unknown;
}

export type RowExpectation = 'one' | 'at-least-one' | 'any';

export class WriteDidNotLandError extends Error {
  readonly described: DescribedError;
  readonly label: string;
  constructor(label: string, described: DescribedError) {
    super(described.message);
    this.name = 'WriteDidNotLandError';
    this.described = described;
    this.label = label;
  }
}

/**
 * Run a write, prove it landed, then refresh the surfaces that read it.
 *
 * @param label   what the user was doing, in their words — it goes in the
 *                message they read, so "assign the account manager", not
 *                "update new_account_sop".
 * @param write   a supabase write that MUST end in `.select(...)`. Without it
 *                `data` comes back null and this function cannot tell a
 *                successful write from one that matched nothing, which is the
 *                entire defect this module exists to close.
 * @param touches tables the write changed. Readers registered against any of
 *                them re-read once the write is proven.
 * @param expect  how many rows the write should have affected. 'one' is the
 *                right answer for a single-row update keyed on its primary
 *                key, which is most of them. 'any' opts out of the proof and
 *                is only correct where zero rows is a legitimate outcome —
 *                say why at the call site.
 *
 * @returns the affected rows.
 * @throws WriteDidNotLandError — carrying a DescribedError, so a modal can
 *         render `.described.message` and switch on `.described.hint`.
 */
export async function commitWrite<T>({
  label,
  write,
  touches,
  expect = 'one',
}: {
  label: string;
  write: PromiseLike<WriteResult<T>>;
  touches: readonly ReaderTable[];
  expect?: RowExpectation;
}): Promise<T[]> {
  const { data, error } = await write;

  if (error) {
    throw new WriteDidNotLandError(label, describeError(error, `Could not ${label}.`));
  }

  const rows = data ?? [];

  if (expect !== 'any' && rows.length === 0) {
    // The write was accepted and changed nothing. Almost always a row-level
    // security policy that permits SELECT and refuses UPDATE, or an id that no
    // longer matches. Both are failures the user must see, because the value
    // they are looking at is the one they typed and not the one stored.
    throw new WriteDidNotLandError(label, {
      message:
        `Nothing was saved when trying to ${label}. Your change is still on screen but it did ` +
        `not reach the database — most likely you do not have permission to change this record. ` +
        `Nothing has been lost; copy anything you typed before leaving the page.`,
      detail: 'The write was accepted and affected zero rows.',
      hint: 'TCS_WRITE_AFFECTED_NO_ROWS',
    });
  }

  if (expect === 'one' && rows.length > 1) {
    throw new WriteDidNotLandError(label, {
      message:
        `That change would have affected ${rows.length} records instead of one, so it was ` +
        `stopped. Nothing was saved.`,
      detail: `Expected exactly one affected row, got ${rows.length}.`,
      hint: 'TCS_WRITE_AFFECTED_MANY_ROWS',
    });
  }

  refreshReaders(touches);
  return rows;
}

/**
 * Register a loader's refresh against the tables it reads, for the life of the
 * component.
 *
 * `tables` may be an inline array literal — the dependency is the joined key,
 * not the array identity, so a fresh literal each render does not re-register.
 * `refresh` DOES need to be referentially stable (useCallback); an unstable one
 * re-registers every render, which is correct but wasteful.
 */
export function useReader(tables: readonly ReaderTable[], refresh: Refresh): void {
  const key = tables.join(' ');
  useEffect(() => registerReader(key.split(' '), refresh), [key, refresh]);
}
